import React from 'react';
import { useTranslation } from 'react-i18next';
import { Grid, makeStyles, TextField } from '@material-ui/core';
import { Autocomplete } from '@material-ui/lab';
import { ActionButton, Typography } from '@klp/design';
import { NumberFormatted } from '@klp/formgenerator/components/NumberFormatted';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { NumericField } from '@klp/prosjekt/components/Vurderingsskjema/components/Form/NumericField';
import type { Avfall, AvfallType } from '../../services/rtkApi';
import { ProsjektAvfallSortert } from './ProsjektAvfallSortert';

const useStyles = makeStyles(() => ({
    numberCell: {
        textAlign: 'right',
    },
}));

type ProsjektAvfallEditRowProps = {
    index: number;
    avfall: Avfall;
    avfallTypes: Array<AvfallType> | undefined;
    totalMengde: number;
    validationErrors: Record<string, string>;
    onAvfallChange: (
        index: number,
        field: 'avfallTypeId' | 'mengde',
        value: number | null,
    ) => void;
    onDeleteRow: (index: number) => void;
};

export function ProsjektAvfallEditRow({
    index,
    avfall,
    avfallTypes,
    totalMengde,
    validationErrors,
    onAvfallChange,
    onDeleteRow,
}: ProsjektAvfallEditRowProps) {
    const { t } = useTranslation();
    const classes = useStyles();
    const selectedType = avfallTypes?.find(
        (type) => type.avfallTypeId === avfall.avfallTypeId,
    );

    // Show placeholder if avfall type is not selected/valid
    const shouldDisplayPlaceholder = selectedType === undefined;
    const placeholder =
        avfall.avfallTypeId !== null
            ? `${avfall.avfallTypeId} - ${avfall.avfallType}`
            : avfall.avfallType;

    return (
        <Grid container={true} spacing={1}>
            <Grid item={true} xs={1}>
                <ProsjektAvfallSortert sortert={avfall.sortert} />
            </Grid>
            <Grid item={true} xs={6}>
                <Autocomplete
                    id={`avfall-type-${index}`}
                    value={selectedType ?? null}
                    options={avfallTypes ?? []}
                    getOptionLabel={(option) =>
                        `${option.avfallTypeId} - ${option.beskrivelse}`
                    }
                    getOptionSelected={(option, value) =>
                        option.avfallTypeId === value?.avfallTypeId
                    }
                    onChange={(_, value) => {
                        onAvfallChange(
                            index,
                            'avfallTypeId',
                            value?.avfallTypeId ?? null,
                        );
                    }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            placeholder={
                                shouldDisplayPlaceholder
                                    ? placeholder
                                    : undefined
                            }
                            variant="outlined"
                            required={true}
                            size="small"
                            error={!!validationErrors[`${index}-type`]}
                            helperText={validationErrors[`${index}-type`]}
                        />
                    )}
                />
            </Grid>
            <Grid item={true} xs={2} className={classes.numberCell}>
                <NumericField
                    id={`avfall-mengde-${index}`}
                    variant="outlined"
                    size="small"
                    isReadOnly={false}
                    isRequired={true}
                    value={avfall.mengde}
                    onValueChange={(value) =>
                        onAvfallChange(index, 'mengde', value.floatValue ?? 0)
                    }
                    error={!!validationErrors[`${index}-mengde`]}
                    helperText={validationErrors[`${index}-mengde`]}
                    inputProps={{ min: 0, step: 0.01 }}
                />
            </Grid>
            <Grid item={true} xs={2} className={classes.numberCell}>
                <Typography>
                    <NumberFormatted
                        value={(avfall.mengde / totalMengde) * 100}
                        decimalScale={1}
                        suffix=" %"
                    />
                </Typography>
            </Grid>
            <Grid item={true} xs={1}>
                <ActionButton
                    variant="text"
                    color="secondary"
                    onClick={() => onDeleteRow(index)}
                    actionType="button"
                    startIcon={<Illustration icon={IconName.DELETE} />}
                    aria-label={t('general.delete')}
                />
            </Grid>
        </Grid>
    );
}
